import { StringData } from "../..";
import { IStringModule } from "../IStringModule";
import { StringModule } from "../StringModule";

/**
 * Central manager for registering, tracking and delegating lifecycle events 
 * to core and UI modules in the system.
 * 
 * Handles scroll, resize, animation frame updates and DOM events.
 */
export class ModuleManager {
  /** All core logic modules (e.g., scroll, in-view, parallax). */
  private modules: StringModule[] = [];

  /** All UI or visual/interaction-based modules (e.g., cursor, split text). */
  private uiModules: StringModule[] = [];

  /**
   * @param data Shared state container for scroll, viewport, etc.
   */
  constructor(private data: StringData) {}

  /**
   * Registers a module into the appropriate group based on its type.
   * @param module The module instance to register.
   */
  register(module: StringModule): void {
    if (module.type === 1) this.modules.push(module);
    if (module.type === 2) this.uiModules.push(module);
  }

  /**
   * Finds the first registered module of the given class/type.
   * @param type The module class constructor.
   * @returns The instance, if found.
   */
  find<T>(type: new (...args: any[]) => T): T | undefined {
    return this.modules.find(m => m instanceof type) as T | undefined;
  }

  /** Invokes `onInit` on all modules. */
  onInit(): void {
    [...this.modules, ...this.uiModules].forEach(m => m.onInit());
  }

  /** Invokes `onFrame` on all modules, passing shared state. */
  onFrame(): void {
    [...this.modules, ...this.uiModules].forEach(m => m.onFrame(this.data));
  }

  /** Invokes `onScroll` on all modules with current scroll state. */
  onScroll(): void {
    [...this.modules, ...this.uiModules].forEach(m => m.onScroll(this.data));
  }

  /** Invokes `onResize` on all modules. */
  onResize(): void {
    [...this.modules, ...this.uiModules].forEach(m => m.onResize());
  }

  /**
   * Delegates mouse movement events to modules.
   * @param e The mousemove event.
   */
  onMouseMove(e: MouseEvent): void {
    [...this.modules, ...this.uiModules].forEach(m => m.onMouseMove(e));
  }

  /**
   * Delegates wheel events to modules.
   * @param e The wheel event.
   */
  onWheel(e: WheelEvent): void {
    [...this.modules, ...this.uiModules].forEach(m => m.onWheel(e));
  }

  /** Notifies all modules that scroll has changed diraction. */
  onDirectionChange(): void {
    [...this.modules, ...this.uiModules].forEach(m => m.onDirectionChange());
  }

  /** Notifies all modules that scroll has started. */
  onScrollStart(): void {
    [...this.modules, ...this.uiModules].forEach(m => m.onScrollStart());
  }

  /** Notifies all modules that scroll has stopped. */
  onScrollStop(): void {
    [...this.modules, ...this.uiModules].forEach(m => m.onScrollStop());
  }

  /** Notifies all modules that scroll axis (horizontal/vertical) has changed. */
  onAxisChange(): void {
    [...this.modules, ...this.uiModules].forEach(m => m.onAxisChange());
  }

  /** Notifies all modules that device type (desktop/mobile) has changed. */
  onDeviceChange(): void {
    [...this.modules, ...this.uiModules].forEach(m => m.onDeviceChange());
  }

  /** Notifies modules of updates to scroll-related configuration. */
  onScrollConfigChange(): void {
    [...this.modules, ...this.uiModules].forEach(m => m.onScrollConfigChange());
  }

  /** Notifies modules of updated global or module-specific settings. */
  onSettingsChange(): void {
    [...this.modules, ...this.uiModules].forEach(m => m.onSettingsChange());
  }

  /**
   * Called when DOM is mutated — e.g. new elements added/removed.
   * @param added Newly added DOM nodes.
   * @param removed Removed DOM nodes.
   */
  onDOMMutate(added: NodeList, removed: NodeList): void {
    [...this.modules, ...this.uiModules].forEach(m => m.onDOMMutate(added, removed));
  }

  /** Cleans up all modules and clears internal lists. */
  destroy(): void {
    [...this.modules, ...this.uiModules].forEach(m => m.destroy());
    this.modules = [];
    this.uiModules = [];
  }

  /**
   * Returns all modules (core + UI) as a flat array.
   */
  get all(): IStringModule[] {
    return [...this.modules, ...this.uiModules];
  }

  /**
   * Returns only core modules (type === 1).
   */
  get core(): IStringModule[] {
    return this.modules;
  }

  /**
   * Returns only UI modules (type === 2).
   */
  get ui(): IStringModule[] {
    return this.uiModules;
  }
}
